import { useCallback, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";

import { Grid } from "@mui/material";

import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";

import { FormContainer } from "../../shared/FormLayout/FormContainer";
import { FormRow } from "../../shared/FormLayout/FormRow";
import { Card } from "../../shared/Card";
import { WorkorderDetails } from "../../shared/interfaces/WorkOrder";
import { Customer } from "../../shared/interfaces/Customer";

import { CustomersByEntityDropdown } from "../../CustomersByEntityDropdown";
import { BulkWorkorderContext } from "./BulkWorkorderContext";
import { ExtraDetails, KeyValue } from "../../ExtraDetails";

interface Props {
  customer?: Customer;
  customerSelected?: boolean;
}

export function WorkorderDetailsForm(props: Props) {
  const { t } = useTranslation();
  const { workorderDetails, updateWorkorderDetailsField } = useContext(BulkWorkorderContext);

  const formMethods = useForm();

  function handleInputChange(event: any) {
    const name = event.target.name;
    const value = event.target.value;

    updateWorkorderDetailsField(name, value);
  }

  useEffect(() => {
    updateWorkorderDetailsField("customerId", props.customer?.id);
    updateWorkorderDetailsField("customerName", props.customer?.name);
  }, [props.customer, updateWorkorderDetailsField]);

  const handleCustomerChange = useCallback(
    (customer?: Customer) => {
      updateWorkorderDetailsField("customerId", customer?.id);
      updateWorkorderDetailsField("customerName", customer?.name);
    },
    [updateWorkorderDetailsField],
  );

  function handleExtraDetailsChange(editedValues: KeyValue[] | undefined) {
    updateWorkorderDetailsField("extraDetail", editedValues);
  }

  function textInputTemplate(key: keyof WorkorderDetails, maxLength?: number) {
    return (
      <InputText
        key={`input-${key}`}
        name={key}
        value={workorderDetails[key]?.toString() || ""}
        onChange={handleInputChange}
        maxLength={maxLength}
      />
    );
  }

  function textAreaInputTemplate(key: keyof WorkorderDetails) {
    return (
      <InputTextarea
        key={key}
        name={key}
        value={workorderDetails[key]?.toString()}
        onChange={handleInputChange}
        autoResize
        className="w-100"
      />
    );
  }

  return (
    <Grid container>
      <Grid item xs={12} lg={6.5} mr={{ lg: 2 }} mb={{ xs: 2, lg: 0 }}>
        <Card title={t("workorders.workorderDetails")}>
          <FormContainer>
            <FormRow
              required
              label={t("customer.customer")}
              value={
                props.customerSelected ? (
                  props.customer?.name
                ) : (
                  <CustomersByEntityDropdown
                    selectedCustomerId={workorderDetails.customerId}
                    entity="workorder"
                    onSelect={handleCustomerChange}
                    hideLabel={true}
                    register={formMethods.register}
                    formState={formMethods.formState}
                    setValue={formMethods.setValue}
                  />
                )
              }
            />
            <FormRow label={t("workorders.clientReference")} value={textInputTemplate("clientId", 50)} />
            <FormRow label={t("common.projectCode")} value={textInputTemplate("projectCode", 50)} />
            <FormRow label={t("common.summary")} value={textInputTemplate("description", 200)} />
            <FormRow label={t("common.detailedDescription")} value={textAreaInputTemplate("longDescription")} />
          </FormContainer>
        </Card>
      </Grid>
      <Grid item xs={12} lg={5}>
        <Card title={t("workorders.extraDetails")}>
          <ExtraDetails data={workorderDetails.extraDetail} editable onEdit={handleExtraDetailsChange} />
        </Card>
      </Grid>
    </Grid>
  );
}
